import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LogoHeader from '../components/LogoHeader';
import { useAntiCheat } from '../hooks/useAntiCheat';
import { useDispatch, useSelector } from 'react-redux';
import { getTestById } from 'thunks/test/actions';
import { getExamSession, clearAllData, hasSessionActivity } from '../utils/examStorage';
import { getRedirectParams, buildStartPageUrl } from '../utils/urlUtils';
import checkLogin from '../utils/userLogin';
import { getCurrentUserId } from '../utils/examStorage';

// Loading Component
const LoadingState = () => (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <LogoHeader />
        <div className="flex justify-center items-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading exam details...</p>
          </div>
        </div>
      </div>
    );

// No Test Selected Component
const NoTestSelected = () => (
  <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
    <LogoHeader />
    <div className="flex justify-center items-center h-screen">
      <div className="text-center">
        <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">No Test Selected</h2>
        <p className="text-gray-600 mb-4">
          Please use a valid test URL to access an exam.
        </p>
        <div className="space-x-3">
          <button 
            onClick={() => window.open('https://theexamhub.com', '_blank')}
            className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 font-medium"
          >
            Visit Website
          </button>
        </div>
      </div>
    </div>
  </div>
);

// Error State Component
const ErrorState = ({ testId, error, isInvalidUrl }) => (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <LogoHeader />
        <div className="flex justify-center items-center h-screen">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          {isInvalidUrl ? 'Invalid URL' : 'Error Loading Exam'}
        </h2>
        <p className="text-gray-600 mb-4">
          {isInvalidUrl 
            ? `The test ID "${testId}" is not valid or does not exist. Please use a valid test URL.`
            : testId ? `Could not load exam with ID: ${testId}` : 'Could not load exam data'
          }
        </p>
        {!isInvalidUrl && (
          <p className="text-sm text-gray-500 mb-4">
            {error}
          </p>
        )}
        <div className="space-x-3">
          {isInvalidUrl ? (
            <button 
              onClick={() => window.location.href = 'https://theexamhub.com'}
              className="bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 font-medium"
            >
              Visit Website
            </button>
          ) : (
            <>
            <button 
              onClick={() => window.location.reload()}
              className="btn-primary"
            >
              Try Again
              </button>
              <button 
                onClick={() => window.history.back()}
                className="bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 font-medium"
              >
                Go Back
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  </div>
);

// Pending Session Modal Component
const PendingSessionModal = ({ session, onContinue, onStartNew, onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
      <div className="flex items-center mb-4">
        <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
          <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-900">Pending Exam Session</h3>
      </div>
      
      <div className="mb-6">
        <p className="text-gray-600 mb-4">
          We found an active exam session from your previous attempt. You can continue from where you left off or start a new exam.
        </p>
        
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <h4 className="font-medium text-gray-900 mb-2">Session Details:</h4>
          <div className="text-sm text-gray-600 space-y-1">
            <div>• Started: {new Date(session.examStartTime).toLocaleString()}</div>
            <div>• Time Remaining: {Math.floor(session.timeRemaining / 60)}m {session.timeRemaining % 60}s</div>
            <div>• Questions Answered: {Object.keys(session.answers || {}).filter(id => session.answers[id]?.selectedOption !== null).length}</div>
          </div>
        </div>
      </div>
      
      <div className="flex space-x-3">
        <button
          onClick={onContinue}
          className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 font-medium"
        >
          Continue Session
        </button>
        <button
          onClick={onStartNew}
          className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 font-medium"
        >
          Start New
            </button>
          </div>
        </div>
      </div>
    );

// Main Exam Content Component
const ExamContent = ({ examData, testId, instructionsRead, setInstructionsRead, activeTab, setActiveTab, handleStartExam }) => {
  // Calculate total questions from all sections
  const totalQuestions = examData.sections.reduce((total, section) => total + section.questions.length, 0);
  
  // Calculate total marks
  const totalMarks = totalQuestions * examData.posMarks;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <LogoHeader />
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Exam Header */}
        <div className="card p-6 mb-6">
          <div className="text-center">
            {testId && (
              <div className="mb-3">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Test ID: {testId}
                </span>
              </div>
            )}
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {examData.title}
            </h1>
            <p className="text-gray-600 mb-4">Exam Type: {examData.type}</p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div className="bg-blue-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-blue-600">
                  {totalQuestions}
                </div>
                <div className="text-sm text-blue-800">Total Questions</div>
              </div>
              <div className="bg-green-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-green-600">
                  {examData.duration} min
                </div>
                <div className="text-sm text-green-800">Duration</div>
              </div>
              <div className="bg-purple-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-purple-600">
                  {totalMarks}
                </div>
                <div className="text-sm text-purple-800">Total Marks</div>
              </div>
              <div className="bg-orange-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-orange-600">
                  {examData.languages?.length || 0}
                </div>
                <div className="text-sm text-orange-800">Languages</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="card">
              {/* Tab Navigation */}
              <div className="border-b border-gray-200">
                <nav className="flex space-x-8 px-6 pt-6">
                  <button
                    onClick={() => setActiveTab('instructions')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'instructions'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Instructions
                  </button>
                  <button
                    onClick={() => setActiveTab('sections')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'sections'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Exam Sections
                  </button>
                  <button
                    onClick={() => setActiveTab('guidelines')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'guidelines'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Guidelines
                  </button>
                  <button
                    onClick={() => setActiveTab('system')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'system'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    System Requirements
                  </button>
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {activeTab === 'instructions' && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      General Instructions
                    </h3>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <span className="text-sm font-medium text-yellow-800">
                          Please read all instructions carefully before starting the examination
                        </span>
                      </div>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      <ol className="list-decimal list-inside space-y-3">
                        {examData.processedInstructions?.en?.map((instruction, index) => (
                          <li key={index} className="text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: instruction }}/>
                        ))}
                      </ol>
                    </div>
                  </div>
                )}

                {activeTab === 'sections' && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Examination Sections
                    </h3>
                    <div className="space-y-4">
                      {examData.sections.map((section, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-center">
                            <div>
                              <h4 className="font-medium text-gray-900">{section.subjectName}</h4>
                              <p className="text-sm text-gray-600">
                                {section.questions?.length || 0} questions
                              </p>
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-semibold text-blue-600">
                                {section.questions?.length * examData.posMarks || 0} marks
                              </div>
                              <div className="text-xs text-gray-500">Maximum</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'guidelines' && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Examination Guidelines
                    </h3>
                    <div className="max-h-80 overflow-y-auto">
                      <ul className="space-y-3">
                        <li className="flex items-start">
                          <svg className="w-4 h-4 text-green-500 mt-1 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-gray-700">Read each question carefully before selecting your answer.</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-4 h-4 text-green-500 mt-1 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-gray-700">Use the language toggle to switch between available languages.</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-4 h-4 text-green-500 mt-1 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-gray-700">Manage your time effectively across all sections.</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-4 h-4 text-green-500 mt-1 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-gray-700">Review your answers before final submission.</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-4 h-4 text-green-500 mt-1 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-gray-700">Do not engage in any unfair practices during the examination.</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}

                {activeTab === 'system' && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      System Requirements
                    </h3>
                    <div className="max-h-80 overflow-y-auto">
                      <ul className="space-y-3">
                        <li className="flex items-start">
                          <svg className="w-4 h-4 text-blue-500 mt-1 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-gray-700">Operating System: Windows 7 or higher / macOS 10.12 or higher / Linux</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-4 h-4 text-blue-500 mt-1 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-gray-700">Browser: Chrome 80+, Firefox 75+, Safari 13+, Edge 80+</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-4 h-4 text-blue-500 mt-1 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-gray-700">Internet: Stable broadband connection (minimum 1 Mbps)</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-4 h-4 text-blue-500 mt-1 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-gray-700">Screen Resolution: Minimum 1024x768 pixels</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-4 h-4 text-blue-500 mt-1 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-gray-700">JavaScript and Cookies must be enabled</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-4 h-4 text-blue-500 mt-1 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-gray-700">Disable popup blockers for this site</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Exam Details
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Exam Type</label>
                  <div className="text-gray-900">{examData.type}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Duration</label>
                  <div className="text-gray-900">{examData.duration} minutes</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Positive Marks</label>
                  <div className="text-gray-900">{examData.posMarks} per question</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Negative Marks</label>
                  <div className="text-gray-900">{examData.negMarks} per wrong answer</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Languages</label>
                  <div className="text-gray-900">{examData.languages?.join(', ') || 'English'}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Max Attempts</label>
                  <div className="text-gray-900">{examData.maxAttempts || 1}</div>
                </div>
              </div>

              <div className="mt-8">
                <div className="flex items-center mb-4">
                  <input
                    type="checkbox"
                    id="readInstructions"
                    checked={instructionsRead}
                    onChange={(e) => setInstructionsRead(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="readInstructions" className="ml-2 block text-sm text-gray-700">
                    I have read and understood all instructions
                  </label>
                </div>

                <button
                  onClick={handleStartExam}
                  disabled={!instructionsRead}
                  className={`w-full py-3 px-4 rounded-lg font-medium text-white ${
                    instructionsRead
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : 'bg-gray-400 cursor-not-allowed'
                  }`}
                >
                  Start Exam
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StartPage = () => {
  const [instructionsRead, setInstructionsRead] = useState(false);
  const [activeTab, setActiveTab] = useState('instructions');
  const [pendingSession, setPendingSession] = useState(null);
  const [showPendingSessionModal, setShowPendingSessionModal] = useState(false);
  const [testId, setTestId] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { requestFullscreen } = useAntiCheat();

  const { data, loading, error } = useSelector(state => state.test);
  const userData = useSelector(state => state.me.data);
  const [examData, setExamData] = useState(null);
  const dispatch = useDispatch();

  // Get userId from Redux store or fallback
  const userId = userData?._id || getCurrentUserId();

  // Get test ID from URL parameters only
  useEffect(() => {
    const urlTestId = searchParams.get('test');
    setTestId(urlTestId);
  }, [searchParams]);

  useEffect(() => {
    if (testId && !data) {
      // Check if user is authenticated before making API call
      const isAuthenticated = checkLogin();
      if (!isAuthenticated) {
        // If not authenticated, show error or redirect to login
        setExamData(null);
        return;
      }
      
      dispatch(getTestById(testId, (res, err) => {
        if (!err) {
          // if (res && (res.error)) {
          //   setExamData(null);
          // } else {
          // }
          setExamData(res);
        }
      }));
    } else if (data) {
      setExamData(data);
    }
  }, [data, dispatch, testId]);

  // Check for pending session when exam data is loaded
  useEffect(() => {
    const checkPendingSession = async () => {
      if (examData && userId) {
        try {
          // Pass userId to get user-specific sessions
          const session = await getExamSession(examData._id, userId);
          if (session && session.status === 'active' && hasSessionActivity(session)) {
            setPendingSession(session);
            setShowPendingSessionModal(true);
            console.log('Found pending session with activity for userId:', userId);
          } else if (session && session.status === 'active' && !hasSessionActivity(session)) {
            // Clean up orphaned session without activity
            console.log('Found orphaned session without activity, cleaning up...');
            await clearAllData();
          }
        } catch (error) {
          console.error('Error checking pending session:', error);
        }
      }
    };

    checkPendingSession();
  }, [examData, userId]);

  const handleStartExam = async () => {
    if (!instructionsRead) {
      alert('Please read all instructions before starting the exam.');
      return;
    }

    try {
      await requestFullscreen();
      navigate('/exam');
    } catch (error) {
      console.error('Failed to start exam:', error);
    }
  };

  const handleContinueSession = () => {
                  setShowPendingSessionModal(false);
                  navigate('/exam');
  };

  const handleStartNewSession = async () => {
                  try {
                    await clearAllData();
                    setShowPendingSessionModal(false);
                    setPendingSession(null);
                    // Optionally reload the page to ensure fresh state
                    window.location.reload();
                  } catch (error) {
                    console.error('Error clearing session:', error);
                    setShowPendingSessionModal(false);
                  }
  };

  // Determine which component to render based on state
  const renderContent = () => {
    // No test ID provided
    if (!testId) {
      return <NoTestSelected />;
    }

    // Error state - check this first to override loading state
    if (error || (testId && !examData && !loading)) {
      const isInvalidUrl = testId && !examData && !loading;
      return <ErrorState testId={testId} error={error} isInvalidUrl={isInvalidUrl} />;
    }

    // Loading state - only show if no error and no data
    if (!examData || loading) {
      return <LoadingState />;
    }

    // Main exam content
    return (
      <ExamContent 
        examData={examData}
        testId={testId}
        instructionsRead={instructionsRead}
        setInstructionsRead={setInstructionsRead}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        handleStartExam={handleStartExam}
      />
    );
  };

  return (
    <>
      {renderContent()}
      
      {/* Pending Session Modal */}
      {showPendingSessionModal && pendingSession && (
        <PendingSessionModal 
          session={pendingSession}
          onContinue={handleContinueSession}
          onStartNew={handleStartNewSession}
          onClose={() => setShowPendingSessionModal(false)}
        />
      )}
    </>
  );
};

export default StartPage; 